import {Product, User} from '@hconnect/apiclient'
import {Typography} from '@hconnect/uikit'
import {Button} from '@hm/fundamental'
import {Box} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React, {useState} from 'react'

import {useTranslation} from '../../hooks/useTranslation'
import {Permission} from '../../modules/LoggedInUser'
import {RoleAssignment} from '../../modules/ManageUsers.selectors'

import {InvitationDialog} from './dialogs/InvitationDialog'
import {useInvitationProducts} from './hooks/useInvitationProducts'
import {InviteUserButton} from './InviteUserButton'

interface InviteUserButtonsProps {
  user?: User | null
  products: Product[]
  refetchUser?: () => void
  variant?: 'text' | 'outlined' | 'contained'
  roles?: RoleAssignment[]
  'data-test-id': string
  loggedInUserPermissions?: Permission[] | null
  showNewUserProfile?: boolean
}

export const hasPassword = (user: User | null): boolean => {
  if (hasLoginDate(user)) return true
  // If the invitation is not required, the user will always have a password
  // If the user is invited, the password would be sent in the invitation
  return !!user?.isInvited
}

export const hasLoginDate = (user: User | null): boolean => {
  return !!user?.lastLoginDate && user?.lastLoginDate !== '0001-01-01'
}

export const InviteUserButtons = ({
  user,
  products,
  refetchUser,
  variant,
  roles,
  'data-test-id': dataTestId,
  loggedInUserPermissions,
  showNewUserProfile
}: InviteUserButtonsProps) => {
  const {t, i18n} = useTranslation()
  const [isInviting, setIsInviting] = useState(false)
  const [showInvitationDialog, setShowInvitationDialog] = useState(false)
  const {filteredProducts} = useInvitationProducts(products, loggedInUserPermissions, roles)

  if (!user) {
    return null
  }

  const isUserInvited = (): boolean => {
    return user.isInvited
  }

  return (
    <div>
      <Box
        display="flex"
        flexDirection={showNewUserProfile ? 'column' : 'row'}
        alignItems="flex-start"
        data-test-id={dataTestId}
        mt={showNewUserProfile ? 0 : 3}
      >
        {isUserInvited() ? (
          <Box display="flex" alignItems="top" data-test-id="invite-successful" mr={2}>
            <CheckCircleIcon style={{marginTop: '-3px', color: '#7f9ba6', fontSize: '29px'}} />
            <Box ml={1}>
              <Typography color="secondary" style={{fontWeight: 'bold'}}>
                {`${t('createUser.invitationSent')}`}
              </Typography>
              <Typography color="secondary" variant="subtitle2">
                {user.invitedOn ? new Date(user.invitedOn).toLocaleDateString(i18n.language) : ''}
              </Typography>
            </Box>
          </Box>
        ) : (
          ''
        )}
        {!showNewUserProfile &&
          filteredProducts.map((product, key) => (
            <InviteUserButton
              data-test-id={dataTestId}
              key={key}
              product={product}
              variant={variant}
              user={user}
              refetchUser={refetchUser}
              isInviting={isInviting}
              setIsInviting={setIsInviting}
              showNewUserProfile={showNewUserProfile}
            />
          ))}
        {showNewUserProfile && (
          <Button
            variant="tertiary"
            onClick={() => setShowInvitationDialog(true)}
            className={`ml-${isUserInvited() ? '5' : '0'}`}
          >
            {isUserInvited() ? 'Invite to more products' : 'Invite to products'}
          </Button>
        )}
      </Box>
      {showInvitationDialog && (
        <InvitationDialog
          open={showInvitationDialog}
          setOpenModal={setShowInvitationDialog}
          products={products}
          data-test-id={dataTestId}
          user={user}
          refetchUser={refetchUser}
          roles={roles}
          loggedInUserPermissions={loggedInUserPermissions}
          onClose={() => setShowInvitationDialog(false)}
        />
      )}
    </div>
  )
}
